import React, { useState, useEffect } from 'react';
import { useParams, useHistory, useLocation } from 'react-router-dom';
import { Fetcher } from '@this/src/util';
import { Button } from '@this/shared/ui/inputs/button';
import { Datepicker } from '@this/shared/ui/inputs/datepicker';
import { Loading } from '@this/shared/ui/feedbacks/loading';
import { Modal } from '@this/shared/ui/feedbacks/modal';
import OnlineApplication from '@this/src/domain/online_application/online_application';
import moment from 'moment';
import { OnlineApplicationView } from './shared/online_application_view';
import { ContactFields } from './shared/contact_fields';
import { SubmitterFields } from './shared/submitter_fields';
import { BankAccountFields } from './shared/bank_account_fields';
import { AgreementCheckbox } from './shared/agreement_checkbox';
import { ExpirationMessage } from './shared/expiration_message';
import type { ApplicationData, FormInputState, ValidationErrors } from './shared/types';
import { API_ENDPOINTS, ERROR_MESSAGES } from './shared/types';
import { validateField, validateAllFields, hasErrors, type ValidationFieldName } from './shared/validation';
import { onlineApplicationStyles } from './shared/styles';

const OnlineApplicationForm: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const history = useHistory();
  const location = useLocation<FormInputState>();
  const [loading, setLoading] = useState(true);
  const [application, setApplication] = useState<OnlineApplication | null>(null);
  const [errors, setErrors] = useState<ValidationErrors>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // 編集可能なフィールド
  const [contactNameKana, setContactNameKana] = useState('');
  const [contactName, setContactName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactTel, setContactTel] = useState('');
  const [startDesiredMonth, setStartDesiredMonth] = useState<moment.Moment | null>(null);
  const [isAgreed, setIsAgreed] = useState(false);
  const [isApplicantConfirmed, setIsApplicantConfirmed] = useState(false);
  const [submitterCompanyName, setSubmitterCompanyName] = useState('');
  const [submitterDepartment, setSubmitterDepartment] = useState('');
  const [submitterName, setSubmitterName] = useState('');
  const [submitterEmail, setSubmitterEmail] = useState('');

  // 返金口座情報
  const [bankName, setBankName] = useState('');
  const [branchName, setBranchName] = useState('');
  const [accountType, setAccountType] = useState('');
  const [accountHolderName, setAccountHolderName] = useState('');
  const [accountNumber, setAccountNumber] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await Fetcher.get<{ success: boolean; data: ApplicationData; error?: string }>(
          API_ENDPOINTS.USER.GET_BY_TOKEN(token)
        );
        if (response.success && response.data) {
          setApplication(new OnlineApplication(response.data));

          // location.stateからの値があればそちらを優先、なければサーバーからのデータを使用
          if (location.state) {
            // 確認画面から戻ってきた場合
            setContactNameKana(location.state.contactNameKana || '');
            setContactName(location.state.contactName || '');
            setContactEmail(location.state.contactEmail || '');
            setContactTel(location.state.contactTel || '');
            if (location.state.startDesiredMonth) {
              const parsed = moment(location.state.startDesiredMonth, 'YYYY年M月');
              if (parsed.isValid()) {
                setStartDesiredMonth(parsed.startOf('month'));
              }
            }
            setSubmitterCompanyName(location.state.submitterCompanyName || '');
            setSubmitterDepartment(location.state.submitterDepartment || '');
            setSubmitterName(location.state.submitterName || '');
            setSubmitterEmail(location.state.submitterEmail || '');
            setBankName(location.state.bankName || response.data.billing?.bank_name || '');
            setBranchName(location.state.branchName || response.data.billing?.branch_name || '');
            const accountTypeFromServer =
              response.data.billing?.account_type === 'ordinary'
                ? '1'
                : response.data.billing?.account_type === 'checking'
                ? '2'
                : '';
            setAccountType(location.state.accountType || accountTypeFromServer);
            setAccountHolderName(
              location.state.accountHolderName || response.data.billing?.account_holder_name || ''
            );
            setAccountNumber(location.state.accountNumber || response.data.billing?.account_number || '');
          } else {
            // 初回アクセス時
            const firstCompany = response.data.companies[0];
            if (firstCompany) {
              setContactNameKana(firstCompany.contact_name_kana || '');
              setContactName(firstCompany.contact_name || '');
              setContactEmail(firstCompany.contact_email || '');
              setContactTel(firstCompany.contact_tel || '');
            }
            if (response.data.start_desired_month) {
              const parsed = moment(response.data.start_desired_month);
              if (parsed.isValid()) {
                setStartDesiredMonth(parsed.startOf('month'));
              }
            }
            if (response.data.billing) {
              setBankName(response.data.billing.bank_name || '');
              setBranchName(response.data.billing.branch_name || '');
              const accountType =
                response.data.billing.account_type === 'ordinary'
                  ? '1'
                  : response.data.billing.account_type === 'checking'
                  ? '2'
                  : '';
              setAccountType(accountType);
              setAccountHolderName(response.data.billing.account_holder_name || '');
              setAccountNumber(response.data.billing.account_number || '');
            }
          }
        } else {
          setErrorMessage(response.error || ERROR_MESSAGES.FETCH_FAILED);
        }
      } catch (error) {
        setErrorMessage(error.response?.data?.error || ERROR_MESSAGES.FETCH_FAILED);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [token]);

  // バリデーション
  const handleFieldValidation = (field: ValidationFieldName, value: string) => {
    const error = validateField(field, value);
    const newErrors = { ...errors };

    if (error) {
      newErrors[field as keyof ValidationErrors] = error;
    } else {
      delete newErrors[field as keyof ValidationErrors];
    }

    setErrors(newErrors);
    return newErrors;
  };

  const handleBlur = (field: ValidationFieldName) => {
    setTouched({ ...touched, [field]: true });
    handleFieldValidation(
      field,
      field === 'contactNameKana'
        ? contactNameKana
        : field === 'contactName'
        ? contactName
        : field === 'contactEmail'
        ? contactEmail
        : field === 'contactTel'
        ? contactTel
        : field === 'submitterCompanyName'
        ? submitterCompanyName
        : field === 'submitterDepartment'
        ? submitterDepartment
        : field === 'submitterName'
        ? submitterName
        : field === 'submitterEmail'
        ? submitterEmail
        : field === 'bankName'
        ? bankName
        : field === 'branchName'
        ? branchName
        : field === 'accountType'
        ? accountType
        : field === 'accountHolderName'
        ? accountHolderName
        : field === 'accountNumber'
        ? accountNumber
        : ''
    );
  };

  // 確認画面への遷移
  const handleConfirm = () => {
    if (application && application.expirationDate) {
      const expirationEndOfDay = new Date(application.expirationDate);
      expirationEndOfDay.setHours(23, 59, 59, 999);
      if (expirationEndOfDay < new Date()) {
        setErrorMessage(
          '申込有効期限切れです。\nお手数ですが、担当者までご連絡くださいますようお願いいたします。'
        );
        setShowErrorModal(true);
        return;
      }
    }

    setTouched({
      contactNameKana: true,
      contactName: true,
      contactEmail: true,
      contactTel: true,
      submitterCompanyName: true,
      submitterDepartment: true,
      submitterName: true,
      submitterEmail: true,
      bankName: true,
      branchName: true,
      accountType: true,
      accountHolderName: true,
      accountNumber: true
    });

    // バリデーション実行
    const newErrors = validateAllFields({
      contactNameKana,
      contactName,
      contactEmail,
      contactTel,
      submitterCompanyName,
      submitterDepartment,
      submitterName,
      submitterEmail,
      bankName,
      branchName,
      accountType,
      accountHolderName,
      accountNumber
    });

    setErrors(newErrors);

    if (hasErrors(newErrors)) {
      return;
    }

    // 確認画面にデータを渡す
    history.push(`/online_applications/token/${token}/confirm`, {
      contactNameKana,
      contactName,
      contactEmail,
      contactTel,
      startDesiredMonth: startDesiredMonth ? startDesiredMonth.format('YYYY年M月') : '',
      submitterCompanyName,
      submitterDepartment,
      submitterName,
      submitterEmail,
      bankName,
      branchName,
      accountType,
      accountHolderName,
      accountNumber
    });
  };

  if (loading) {
    return <Loading />;
  }

  if (!application) {
    return (
      <div style={{ ...onlineApplicationStyles.container.main, backgroundColor: 'none' }}>
        <div style={onlineApplicationStyles.message.error.container}>
          <p style={onlineApplicationStyles.message.error.text}>
            {errorMessage && (
              <>
                {errorMessage.split('\n').map((line, index) => (
                  <React.Fragment key={index}>
                    {line}
                    {index < errorMessage.split('\n').length - 1 && <br />}
                  </React.Fragment>
                ))}
                <br />
                {ERROR_MESSAGES.CONTACT_SUPPORT}
              </>
            )}
          </p>
        </div>
      </div>
    );
  }

  const createFieldChangeHandler = (
    setter: React.Dispatch<React.SetStateAction<string>>,
    fieldName: ValidationFieldName
  ) => {
    return (value: string) => {
      setter(value);
      if (touched[fieldName]) {
        handleFieldValidation(fieldName, value);
      }
    };
  };

  // 各フィールドのハンドラー
  const handleContactNameKanaChange = createFieldChangeHandler(setContactNameKana, 'contactNameKana');
  const handleContactNameChange = createFieldChangeHandler(setContactName, 'contactName');
  const handleContactEmailChange = createFieldChangeHandler(setContactEmail, 'contactEmail');
  const handleContactTelChange = createFieldChangeHandler(setContactTel, 'contactTel');
  const handleSubmitterCompanyNameChange = createFieldChangeHandler(
    setSubmitterCompanyName,
    'submitterCompanyName'
  );
  const handleSubmitterDepartmentChange = createFieldChangeHandler(setSubmitterDepartment, 'submitterDepartment');
  const handleSubmitterNameChange = createFieldChangeHandler(setSubmitterName, 'submitterName');
  const handleSubmitterEmailChange = createFieldChangeHandler(setSubmitterEmail, 'submitterEmail');

  const renderStartDesiredMonth = () => {
    return (
      <>
        <h3 style={onlineApplicationStyles.heading.base}>開始希望月</h3>
        <div style={onlineApplicationStyles.section.base}>
          <Datepicker
            picker="month"
            value={startDesiredMonth ? startDesiredMonth.toDate() : undefined}
            onChange={(date: Date | null) => {
              if (date) {
                setStartDesiredMonth(moment(date).startOf('month'));
              } else {
                setStartDesiredMonth(null);
              }
            }}
            placeholder="例: 2025/04"
          />
        </div>
      </>
    );
  };

  return (
    <div style={onlineApplicationStyles.container.main}>
      <ExpirationMessage expirationDate={application.expirationDate} buttonText="入力内容を確認" />

      <OnlineApplicationView
        application={application}
        contactName={contactName}
        contactEmail={contactEmail}
        contactTel={contactTel}
        startDesiredMonth={startDesiredMonth ? startDesiredMonth.format('YYYY年M月') : ''}
        customStartDesiredMonth={renderStartDesiredMonth()}
        customCompanyFields={
          <ContactFields
            contactNameKana={contactNameKana}
            contactName={contactName}
            contactEmail={contactEmail}
            contactTel={contactTel}
            errors={errors}
            touched={touched}
            onContactNameKanaChange={handleContactNameKanaChange}
            onContactNameChange={handleContactNameChange}
            onContactEmailChange={handleContactEmailChange}
            onContactTelChange={handleContactTelChange}
            onBlur={handleBlur}
          />
        }
        customBankAccountFields={
          <BankAccountFields
            bankName={bankName}
            setBankName={setBankName}
            branchName={branchName}
            setBranchName={setBranchName}
            accountType={accountType}
            setAccountType={setAccountType}
            accountHolderName={accountHolderName}
            setAccountHolderName={setAccountHolderName}
            accountNumber={accountNumber}
            setAccountNumber={setAccountNumber}
            errors={errors}
            touched={touched}
            handleBlur={handleBlur}
            handleFieldValidation={handleFieldValidation}
          />
        }
        customSubmitterFields={
          <SubmitterFields
            submitterCompanyName={submitterCompanyName}
            submitterDepartment={submitterDepartment}
            submitterName={submitterName}
            submitterEmail={submitterEmail}
            errors={errors}
            touched={touched}
            onSubmitterCompanyNameChange={handleSubmitterCompanyNameChange}
            onSubmitterDepartmentChange={handleSubmitterDepartmentChange}
            onSubmitterNameChange={handleSubmitterNameChange}
            onSubmitterEmailChange={handleSubmitterEmailChange}
            onBlur={handleBlur}
          />
        }
      />
      {/* 同意チェックボックス */}
      <AgreementCheckbox
        checked={isAgreed}
        onChange={setIsAgreed}
        isApplicantConfirmed={isApplicantConfirmed}
        onApplicantConfirmedChange={setIsApplicantConfirmed}
      />

      <div>
        <div style={onlineApplicationStyles.button.group}>
          <Button color="primary" onClick={handleConfirm} disabled={!isAgreed || !isApplicantConfirmed}>
            入力内容を確認
          </Button>
        </div>
        {hasErrors(errors) && Object.keys(touched).length > 0 && (
          <div style={onlineApplicationStyles.errorMessage.container}>
            <p style={onlineApplicationStyles.errorMessage.text}>
              入力内容にエラーがあります。エラー内容をご確認ください。
            </p>
          </div>
        )}
      </div>

      {/* エラーモーダル */}
      <Modal open={showErrorModal} onClose={() => setShowErrorModal(false)} size="small">
        <div style={onlineApplicationStyles.modal.body}>
          <p style={onlineApplicationStyles.modal.error}>エラー</p>
          <p style={onlineApplicationStyles.modal.message}>
            {errorMessage.split('\n').map((line, index) => (
              <React.Fragment key={index}>
                {line}
                {index < errorMessage.split('\n').length - 1 && <br />}
              </React.Fragment>
            ))}
          </p>
        </div>
      </Modal>
    </div>
  );
};

export default OnlineApplicationForm;
